import { Badge } from "@/components/ui/badge";
import { BarChart3, Star, TrendingUp, BookOpen, Activity, Ruler, Brain, Zap, CreditCard, LogOut, Settings } from "lucide-react";

const FOX_IMG = "https://static.prod-images.emergentagent.com/jobs/75789c9b-0d29-4ec0-9a3b-e1fc05fe9785/images/5b2ab7fd29746777696641592f7d349c0b2930b2e79250b8a28c9a3f926de55d.png";
const RABBIT_IMG = "https://static.prod-images.emergentagent.com/jobs/75789c9b-0d29-4ec0-9a3b-e1fc05fe9785/images/0a91d9734b19b5695dfd77030bf27a3e66a6f003c83fd79a50f7a7cba638e844.png";
const CAT_IMG = "https://static.prod-images.emergentagent.com/jobs/75789c9b-0d29-4ec0-9a3b-e1fc05fe9785/images/4549e7b872e6901b5b93a88129f7881870f873b4350a06594060515fa82348fe.png";

const PawIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <ellipse cx="6" cy="9" rx="2" ry="2.6" />
    <ellipse cx="10.5" cy="6" rx="2" ry="2.6" />
    <ellipse cx="13.5" cy="6" rx="2" ry="2.6" />
    <ellipse cx="18" cy="9" rx="2" ry="2.6" />
    <path d="M12 11c-3.2 0-6 2.2-6 5.2 0 1.7 1.3 3 3 3 1 0 1.8-.5 3-.5s2 .5 3 .5c1.7 0 3-1.3 3-3 0-3-2.8-5.2-6-5.2z" />
  </svg>
);

const NAV_ITEMS = [
  { key: "signals", label: "Signals", labelAr: "الاشارات", icon: <TrendingUp className="w-4 h-4" />, mascot: FOX_IMG },
  { key: "indicators", label: "Indicators", labelAr: "المؤشرات", icon: <BarChart3 className="w-4 h-4" />, mascot: CAT_IMG },
  { key: "morningstar", label: "Morning", labelAr: "نجمة", icon: <Star className="w-4 h-4" />, mascot: RABBIT_IMG },
  { key: "strategies", label: "Strategies", labelAr: "استراتيجيات", icon: <BookOpen className="w-4 h-4" />, mascot: FOX_IMG },
  { key: "elliott", label: "Elliott", labelAr: "موجي", icon: <Activity className="w-4 h-4" />, mascot: CAT_IMG },
  { key: "fibonacci", label: "Fibonacci", labelAr: "فيبوناتشي", icon: <Ruler className="w-4 h-4" />, mascot: RABBIT_IMG },
  { key: "advanced", label: "Advanced", labelAr: "متقدم", icon: <Brain className="w-4 h-4" />, mascot: FOX_IMG },
  { key: "tia-scalping", label: "Tia Scalp", labelAr: "سكالب تيا", icon: <Zap className="w-4 h-4" />, mascot: RABBIT_IMG },
];

export function KawaiiNav({ activePage, setActivePage, lang, user, membershipData, onLogout, onAdmin, onPayPal }) {
  // Tier-based nav filtering:
  // - User has tier_advanced or signal_tier=advanced → full nav
  // - User has tier_basic only → handled outside (BasicTierView)
  // - Else (education only / no tier) → Strategies tab only
  const hasAdvanced = user?.tier_advanced || user?.signal_tier === "advanced";
  const hasBasic = user?.tier_basic || user?.signal_tier === "basic";
  const isEducationOnly = user?.role !== "admin" && !hasAdvanced && !hasBasic;
  const visibleNav = isEducationOnly
    ? NAV_ITEMS.filter((i) => i.key === "strategies")
    : NAV_ITEMS;
  return (
    <div className="bg-gradient-to-r from-pink-200 via-yellow-100 to-cyan-200 border-b-4 border-pink-300 shadow-md">
      {/* Top row: logo + right actions */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 gap-2">
        {/* Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <img src="/logo.png" alt="TTANALYSIS" className="w-8 h-8 sm:w-10 sm:h-10 drop-shadow" />
          <span className="text-sm sm:text-lg font-extrabold text-pink-600 tracking-wide">TTANALYSIS</span>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {membershipData && (
            <Badge
              className={`text-[10px] sm:text-[11px] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-semibold ${
                membershipData.membership_active
                  ? "bg-gradient-to-r from-emerald-400 to-green-500 text-white"
                  : "bg-gradient-to-r from-red-400 to-pink-500 text-white"
              }`}
              data-testid="membership-badge"
            >
              {membershipData.membership_active ? (membershipData.time_remaining || "Active") : "Expired"}
            </Badge>
          )}
          {membershipData && !membershipData.membership_active && (
            <button
              onClick={onPayPal}
              className="text-[10px] sm:text-[11px] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 text-white font-semibold shadow"
              data-testid="paypal-btn"
            >
              <CreditCard className="w-3 h-3 inline mr-0.5" />$10
            </button>
          )}
          {user?.role === "admin" && (
            <button
              onClick={onAdmin}
              className="p-1.5 sm:p-2 rounded-full bg-white/80 hover:bg-pink-100 text-pink-600 border-2 border-pink-200 hover:border-pink-400 transition-all"
              title="Admin"
              data-testid="admin-btn"
            >
              <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          )}
          <button
            onClick={onLogout}
            className="p-1.5 sm:p-2 rounded-full bg-white/80 hover:bg-red-100 text-red-500 border-2 border-red-200 hover:border-red-400 transition-all"
            title="Logout"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* Tabs row (scrollable on mobile) */}
      <div className="flex justify-center gap-1.5 sm:gap-2 px-2 pb-2 sm:pb-3 overflow-x-auto">
        {visibleNav.map((item) => {
          const active = activePage === item.key;
          return (
            <button
              key={item.key}
              onClick={() => setActivePage(item.key)}
              className={`relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold transition-all border-2 shrink-0 ${
                active
                  ? "bg-gradient-to-r from-pink-400 to-purple-400 text-white border-pink-500 shadow-lg shadow-pink-300/60 scale-105"
                  : "bg-white/80 text-pink-700 border-pink-200 hover:bg-pink-100 hover:border-pink-300"
              }`}
              data-testid={`nav-${item.key}`}
            >
              <img src={item.mascot} alt="" className="w-5 h-5 sm:w-6 sm:h-6 rounded-full ring-2 ring-white shadow" />
              <span className="whitespace-nowrap">{lang === "ar" ? item.labelAr : item.label}</span>
              {active && <PawIcon className="absolute -top-1.5 -right-0.5 w-3.5 h-3.5 sm:w-4 sm:h-4 text-pink-300 rotate-12" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { FOX_IMG, RABBIT_IMG, CAT_IMG };
